import { useMemo, useContext } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { Table } from '@/components/table';
import { Dataset } from '@/types/source-types';
import { Button } from '@/components/ui/button';
import { Edit2, Trash } from 'lucide-react';
import { AuthContext } from '@/context/auth-context';
import { CopyButton } from '@/components/ui/copy-button';

interface DatasetsTableProps {
  datasets: Dataset[];
  loading: boolean;
  onEdit: (dataset: Dataset) => void;
  onDelete: (dataset: Dataset) => void;
  onOpen?: (dataset: Dataset) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

export const DatasetsTable = ({
  datasets,
  loading,
  onEdit,
  onDelete,
  onOpen,
  canEdit = true,
  canDelete = false,
}: DatasetsTableProps) => {
  const { isAdmin } = useContext(AuthContext);
  const columns = useMemo<ColumnDef<Dataset>[]>(() => {
    const cols: ColumnDef<Dataset>[] = [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: ({ row }) => (
          <div className='flex flex-col gap-1'>
            <span className='font-medium'>{row.original.name}</span>
            {isAdmin && (
              <small className='text-xs text-muted-foreground flex items-center gap-1'>
                <span className='truncate max-w-[180px]'>
                  {row.original.id}
                </span>
                <CopyButton
                  text={row.original.id}
                  size='small'
                  aria-label={`Copy dataset id for ${row.original.name}`}
                />
              </small>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <span className='text-muted-foreground'>
            {row.original.description || '—'}
          </span>
        ),
      },
      {
        accessorKey: 'csvCount',
        header: 'CSV Count',
        cell: ({ row }) => row.original.csv_object_keys.length,
      },
      {
        accessorKey: 'updated_at',
        header: 'Last Updated',
        cell: ({ row }) =>
          row.original.updated_at
            ? format(new Date(row.original.updated_at), 'MM/dd/yyyy')
            : '—',
      },
    ];

    if (canEdit || canDelete) {
      cols.push({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className='flex justify-end gap-1'>
            {canEdit && (
              <Button
                variant='ghost'
                size='icon'
                aria-label={`Edit ${row.original.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onEdit(row.original);
                }}
              >
                <Edit2 className='w-4 h-4' />
              </Button>
            )}
            {canDelete && (
              <Button
                variant='ghost'
                size='icon'
                aria-label={`Delete ${row.original.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(row.original);
                }}
              >
                <Trash className='w-4 h-4' />
              </Button>
            )}
          </div>
        ),
        enableSorting: false,
      });
    }

    return cols;
  }, [onDelete, onEdit, isAdmin, canEdit, canDelete]);

  return (
    <Table
      columns={columns}
      data={datasets}
      loading={loading}
      onRowClick={onOpen}
    />
  );
};
